import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMembership } from '../context/MembershipContext';
import { useNotifications } from '../context/NotificationContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Grid3X3, MessageCircle, User, Settings, Camera, Plus, X, 
  LogOut, Lock, Eye, EyeOff, Save, Trash2, WifiOff, Wifi, Ban,
  BadgeCheck, Calendar, Ghost, Users, Crown, Star, Zap, Bell, BellOff,
  Shield, Heart, HelpCircle, Mic, MicOff, Play, Square, MapPinned, Clock,
  MessageSquare, Sparkles, Ruler, ChevronDown, ChevronUp, ChevronRight
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultAvatar = 'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';

const MyProfile = () => {
  const navigate = useNavigate();
  const { user, updateProfile, logout, refreshUser } = useAuth();
  const { hasFeature, checkFeature, membership, refresh: refreshMembership } = useMembership();
  const { permissionGranted, preferences, requestPermission, updatePreferences, isConfigured } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isVisible, setIsVisible] = useState(user?.is_visible !== false);
  const [isIncognito, setIsIncognito] = useState(user?.incognito_mode || false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [togglingIncognito, setTogglingIncognito] = useState(false);
  const fileInputRef = useRef(null);
  const privateFileInputRef = useRef(null);
  
  // Dropdown options from API
  const [dropdownOptions, setDropdownOptions] = useState({
    genders: [],
    heights: [],
    weights: [],
    ages: [],
    ethnicities: [],
    body_types: [],
    positions: [],
    tribes: [],
    states: [],
    // Health & wellness
    hiv_status: [],
    last_tested: [],
    covid_vaccination: [],
    safer_sex: [],
    // Connection & lifestyle
    relationship_status: [],
    looking_for: [],
    pronouns: [],
    expression: [],
    sexuality: [],
    smoking: [],
    drinking: [],
    cannabis: [],
    meet_at: [],
    languages: [],
    icebreaker_prompts: [],
    availability_status: [],
    // New options
    prevention_practices: [],
    sexual_practices: [],
    endowment_cut: [],
    endowment_size: [],
    kinks_freaks: []
  });
  
  const [formData, setFormData] = useState({
    display_name: user?.display_name || '',
    bio: user?.bio || '',  // "About Me"
    age: user?.age?.toString() || '',
    height: user?.height || '',
    weight: user?.weight || '',
    gender: user?.gender || '',
    ethnicity: user?.ethnicity || '',
    body_type: user?.body_type || '',
    position: user?.position || '',
    tribe: user?.tribe || [],  // Multi-select
    looking_for: user?.looking_for || [],  // Changed to multi-select
    interests: user?.interests || [],
    kinks_freaks: user?.kinks_freaks || [],  // New multi-select field
    // Identity
    pronouns: user?.pronouns || '',
    expression: user?.expression || '',
    sexuality: user?.sexuality || '',
    // Stats - Endowment
    endowment_cut: user?.endowment_cut || '',
    endowment_size: user?.endowment_size || '',
    // Health & wellness
    hiv_status: user?.hiv_status || '',
    last_tested: user?.last_tested || '',
    prevention_practices: user?.prevention_practices || [],  // Multi-select
    sexual_practices: user?.sexual_practices || [],  // Multi-select
    // Connection & lifestyle
    relationship_status: user?.relationship_status || '',
    smoking: user?.smoking || '',
    drinking: user?.drinking || '',
    cannabis: user?.cannabis || '',
    meet_at: user?.meet_at || [],  // Changed to multi-select
    languages: user?.languages || [],
    // Icebreaker prompts
    icebreaker_prompt_1: user?.icebreaker_prompt_1 || '',
    icebreaker_1: user?.icebreaker_1 || '',
    icebreaker_prompt_2: user?.icebreaker_prompt_2 || '',
    icebreaker_2: user?.icebreaker_2 || '',
    icebreaker_prompt_3: user?.icebreaker_prompt_3 || '',
    icebreaker_3: user?.icebreaker_3 || '',
    // Local guide
    is_local_guide: user?.is_local_guide || false
  });

  // Availability status (separate state for quick toggle)
  const [availabilityStatus, setAvailabilityStatus] = useState(user?.availability_status || 'just_browsing');
  const [updatingAvailability, setUpdatingAvailability] = useState(false);
  
  // Voice intro state
  const [voiceIntro, setVoiceIntro] = useState(user?.voice_intro || null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const [newInterest, setNewInterest] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Collapsible sections state - default collapsed
  const [expandedSections, setExpandedSections] = useState({
    looking_for: false,
    meet_at: false,
    tribe: false,
    sexual_practices: false,
    kinks_freaks: false,
    prevention_practices: false
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const allExpanded = Object.values(expandedSections).every(v => v);
  
  const toggleAllSections = () => {
    const newState = !allExpanded;
    setExpandedSections({
      looking_for: newState,
      meet_at: newState,
      tribe: newState,
      sexual_practices: newState,
      kinks_freaks: newState,
      prevention_practices: newState
    });
  };

  // Fetch dropdown options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await axios.get(`${API}/dropdown-options`);
        setDropdownOptions(response.data);
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
      }
    };
    fetchOptions();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const dataToSave = { ...formData };
      if (dataToSave.age) {
        dataToSave.age = parseInt(dataToSave.age);
      } else {
        delete dataToSave.age;
      }
      
      await updateProfile(dataToSave);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async () => {
    setTogglingVisibility(true);
    try {
      const newVisibility = !isVisible;
      await axios.put(`${API}/users/visibility`, { is_visible: newVisibility });
      setIsVisible(newVisibility);
      toast.success(newVisibility ? 'You are now visible on the grid' : 'You are now hidden from the grid');
    } catch (error) {
      toast.error('Failed to update visibility');
    } finally {
      setTogglingVisibility(false);
    }
  };

  const handleToggleIncognito = async () => {
    // Check if trying to enable - need to verify feature access
    if (!isIncognito && !checkFeature('incognito_mode', 'Incognito mode')) {
      return;
    }
    
    setTogglingIncognito(true);
    try {
      const newIncognito = !isIncognito;
      await axios.put(`${API}/users/incognito`, { incognito_mode: newIncognito });
      setIsIncognito(newIncognito);
      toast.success(newIncognito ? 'Incognito mode enabled - browse invisibly' : 'Incognito mode disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update incognito mode');
    } finally {
      setTogglingIncognito(false);
    }
  };

  const handlePhotoUpload = async (e, isPrivate = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        await axios.post(`${API}/users/photos`, {
          photo_data: base64,
          is_private: isPrivate
        });
        await refreshUser();
        toast.success('Photo uploaded!');
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (index, isPrivate = false) => {
    try {
      await axios.delete(`${API}/users/photos/${index}?is_private=${isPrivate}`);
      await refreshUser();
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const toggleLookingFor = (item) => {
    setFormData(prev => ({
      ...prev,
      looking_for: prev.looking_for.includes(item)
        ? prev.looking_for.filter(i => i !== item)
        : [...prev.looking_for, item]
    }));
  };

  const addInterest = () => {
    if (newInterest.trim() && !formData.interests.includes(newInterest.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        interests: [...prev.interests, newInterest.trim().toLowerCase()]
      }));
      setNewInterest('');
    }
  };

  const removeInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.filter(i => i !== interest)
    }));
  };

  // Availability status handler
  const handleAvailabilityChange = async (status) => {
    setUpdatingAvailability(true);
    try {
      await axios.put(`${API}/users/availability`, { status });
      setAvailabilityStatus(status);
      const statusLabels = {
        'just_browsing': 'Just Browsing',
        'ready_to_meet': 'Ready to Meet',
        'available_now': 'Available Now'
      };
      toast.success(`Status updated: ${statusLabels[status]}`);
    } catch (error) {
      toast.error('Failed to update availability');
    } finally {
      setUpdatingAvailability(false);
    }
  };

  // Voice intro recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Auto-stop at 30 seconds
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 30) {
            stopRecording();
            return 30;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const saveVoiceIntro = async () => {
    if (!audioBlob) return;
    
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        await axios.post(`${API}/users/voice-intro`, { audio_data: base64 });
        setVoiceIntro(base64);
        setAudioBlob(null);
        await refreshUser();
        toast.success('Voice intro saved!');
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      toast.error('Failed to save voice intro');
    }
  };

  const deleteVoiceIntro = async () => {
    try {
      await axios.delete(`${API}/users/voice-intro`);
      setVoiceIntro(null);
      await refreshUser();
      toast.success('Voice intro deleted');
    } catch (error) {
      toast.error('Failed to delete voice intro');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getPhotoUrl = (photo) => {
    if (photo.startsWith('data:') || photo.startsWith('http')) {
      return photo;
    }
    return `data:image/jpeg;base64,${photo}`;
  };

  const mainPhoto = user?.public_photos?.[0] 
    ? getPhotoUrl(user.public_photos[0])
    : defaultAvatar;

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-[#FF2E63]">My Profile</h1>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="icon"
            onClick={() => setShowLogoutModal(true)}
            className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Profile Photo Section */}
      <div className="relative">
        <div className="w-full aspect-square max-h-[40vh] overflow-hidden">
          <img
            src={mainPhoto}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
        
        <div className="absolute bottom-4 left-4 right-4">
          <h2 className="text-2xl font-bold text-white">
            {user?.display_name || user?.username}
            {user?.age && <span className="font-normal">, {user.age}</span>}
          </h2>
          <p className="text-[#A1A1AA] text-sm">@{user?.username}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 pt-4">
        {/* Quick Help Banner */}
        <button
          data-testid="quick-help-btn"
          onClick={() => navigate('/contact')}
          className="w-full mb-4 p-3 bg-gradient-to-r from-[#FF2E63]/10 to-[#08D9D6]/10 border border-[#FF2E63]/30 rounded-xl flex items-center justify-between hover:from-[#FF2E63]/20 hover:to-[#08D9D6]/20 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF2E63]/20 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-[#FF2E63]" />
            </div>
            <div className="text-left">
              <p className="text-[#EDEDED] font-medium text-sm">Need Help?</p>
              <p className="text-[#A1A1AA] text-xs">Contact us for support, feedback, or to report an issue</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#A1A1AA]" />
        </button>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-white/5 mb-6">
            <TabsTrigger 
              data-testid="profile-tab"
              value="profile" 
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white"
            >
              Profile
            </TabsTrigger>
            <TabsTrigger 
              data-testid="photos-tab"
              value="photos"
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white"
            >
              Photos
            </TabsTrigger>
            <TabsTrigger 
              data-testid="settings-tab"
              value="settings"
              className="data-[state=active]:bg-[#FF2E63] data-[state=active]:text-white"
            >
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            {/* Expand/Collapse All Button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={toggleAllSections}
                className="text-xs text-[#A1A1AA] hover:text-white flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              >
                {allExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Collapse All
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Expand All
                  </>
                )}
              </button>
            </div>
            
            {/* Basic Info */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA]">Basic Info</h3>
              
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Display Name</Label>
                <Input
                  data-testid="display-name-input"
                  value={formData.display_name}
                  onChange={(e) => setFormData(f => ({ ...f, display_name: e.target.value.slice(0, 20) }))}
                  className="bg-white/5 border-white/10 text-white h-12"
                  placeholder="Your display name"
                  maxLength={20}
                />
                <p className="text-xs text-[#52525B]">{formData.display_name?.length || 0}/20 characters</p>
              </div>

              <div className="space-y-2">
                <Label className="text-[#EDEDED]">About Me</Label>
                <Textarea
                  data-testid="bio-input"
                  value={formData.bio}
                  onChange={(e) => setFormData(f => ({ ...f, bio: e.target.value.slice(0, 355) }))}
                  className="bg-white/5 border-white/10 text-white min-h-[100px]"
                  placeholder="Tell others about yourself..."
                  maxLength={355}
                />
                <p className="text-xs text-[#52525B]">{formData.bio?.length || 0}/355 characters</p>
              </div>

              {/* Looking For - Collapsible */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection('looking_for')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <Label className="text-[#EDEDED] cursor-pointer">Looking For</Label>
                  <div className="flex items-center gap-2">
                    {Array.isArray(formData.looking_for) && formData.looking_for.length > 0 && (
                      <span className="text-xs text-[#FF2E63]">{formData.looking_for.length} selected</span>
                    )}
                    {expandedSections.looking_for ? (
                      <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                    )}
                  </div>
                </button>
                {expandedSections.looking_for && (
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {dropdownOptions.looking_for?.map(item => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(formData.looking_for) ? formData.looking_for : [];
                          if (current.includes(item)) {
                            setFormData(f => ({ ...f, looking_for: current.filter(i => i !== item) }));
                          } else {
                            setFormData(f => ({ ...f, looking_for: [...current, item] }));
                          }
                        }}
                        className={`p-2 rounded-lg text-sm transition-all ${
                          Array.isArray(formData.looking_for) && formData.looking_for.includes(item)
                            ? 'bg-[#FF2E63]/20 border-2 border-[#FF2E63] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                )}
                {!expandedSections.looking_for && Array.isArray(formData.looking_for) && formData.looking_for.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.looking_for.map(item => (
                      <span key={item} className="text-xs bg-[#FF2E63]/20 text-[#FF2E63] px-2 py-0.5 rounded-full">{item}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Identity Section */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <User className="w-4 h-4 text-[#08D9D6]" />
                Identity
              </h3>
              
              {/* Gender */}
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Gender</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => setFormData(f => ({ ...f, gender: value }))}
                >
                  <SelectTrigger data-testid="gender-select" className="bg-white/5 border-white/10 h-12">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                    {dropdownOptions.genders.map(g => (
                      <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Pronouns</Label>
                  <Select
                    value={formData.pronouns}
                    onValueChange={(value) => setFormData(f => ({ ...f, pronouns: value }))}
                  >
                    <SelectTrigger data-testid="pronouns-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.pronouns?.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Expression</Label>
                  <Select
                    value={formData.expression}
                    onValueChange={(value) => setFormData(f => ({ ...f, expression: value }))}
                  >
                    <SelectTrigger data-testid="expression-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.expression?.map(e => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Sexuality</Label>
                  <Select
                    value={formData.sexuality}
                    onValueChange={(value) => setFormData(f => ({ ...f, sexuality: value }))}
                  >
                    <SelectTrigger data-testid="sexuality-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select sexuality" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.sexuality?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Position</Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => setFormData(f => ({ ...f, position: value }))}
                  >
                    <SelectTrigger data-testid="position-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.positions?.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Ruler className="w-4 h-4 text-[#FF2E63]" />
                Stats
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Age</Label>
                  <Select
                    value={formData.age}
                    onValueChange={(value) => setFormData(f => ({ ...f, age: value }))}
                  >
                    <SelectTrigger data-testid="age-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select age" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.ages.map(a => (
                        <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Height</Label>
                  <Select
                    value={formData.height}
                    onValueChange={(value) => setFormData(f => ({ ...f, height: value }))}
                  >
                    <SelectTrigger data-testid="height-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select height" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.heights.map(h => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Weight</Label>
                  <Select
                    value={formData.weight}
                    onValueChange={(value) => setFormData(f => ({ ...f, weight: value }))}
                  >
                    <SelectTrigger data-testid="weight-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.weights?.map(w => (
                        <SelectItem key={w} value={w}>{w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Ethnicity</Label>
                  <Select
                    value={formData.ethnicity}
                    onValueChange={(value) => setFormData(f => ({ ...f, ethnicity: value }))}
                  >
                    <SelectTrigger data-testid="ethnicity-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.ethnicities?.map(e => (
                        <SelectItem key={e} value={e}>{e}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Body Type</Label>
                  <Select
                    value={formData.body_type}
                    onValueChange={(value) => setFormData(f => ({ ...f, body_type: value }))}
                  >
                    <SelectTrigger data-testid="body-type-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.body_types?.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Relationship Status</Label>
                  <Select
                    value={formData.relationship_status}
                    onValueChange={(value) => setFormData(f => ({ ...f, relationship_status: value }))}
                  >
                    <SelectTrigger data-testid="relationship-status-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.relationship_status?.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Endowment - Two-part field */}
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Endowment</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    value={formData.endowment_cut}
                    onValueChange={(value) => setFormData(f => ({ ...f, endowment_cut: value }))}
                  >
                    <SelectTrigger data-testid="endowment-cut-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Cut/Uncut" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.endowment_cut?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={formData.endowment_size}
                    onValueChange={(value) => setFormData(f => ({ ...f, endowment_size: value }))}
                  >
                    <SelectTrigger data-testid="endowment-size-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Size" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.endowment_size?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Connection & Lifestyle Section */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Heart className="w-4 h-4 text-[#FF2E63]" />
                Connection & Lifestyle
              </h3>
              
              {/* Meet At - Collapsible */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection('meet_at')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <Label className="text-[#EDEDED] cursor-pointer">Meet At</Label>
                  <div className="flex items-center gap-2">
                    {Array.isArray(formData.meet_at) && formData.meet_at.length > 0 && (
                      <span className="text-xs text-[#FF2E63]">{formData.meet_at.length} selected</span>
                    )}
                    {expandedSections.meet_at ? (
                      <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                    )}
                  </div>
                </button>
                {expandedSections.meet_at && (
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {dropdownOptions.meet_at?.map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(formData.meet_at) ? formData.meet_at : [];
                          if (current.includes(m)) {
                            setFormData(f => ({ ...f, meet_at: current.filter(i => i !== m) }));
                          } else {
                            setFormData(f => ({ ...f, meet_at: [...current, m] }));
                          }
                        }}
                        className={`p-2 rounded-lg text-sm transition-all ${
                          Array.isArray(formData.meet_at) && formData.meet_at.includes(m)
                            ? 'bg-[#FF2E63]/20 border-2 border-[#FF2E63] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                )}
                {!expandedSections.meet_at && Array.isArray(formData.meet_at) && formData.meet_at.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.meet_at.map(m => (
                      <span key={m} className="text-xs bg-[#FF2E63]/20 text-[#FF2E63] px-2 py-0.5 rounded-full">{m}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Tribe - Collapsible */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection('tribe')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <Label className="text-[#EDEDED] cursor-pointer">Tribe</Label>
                  <div className="flex items-center gap-2">
                    {Array.isArray(formData.tribe) && formData.tribe.length > 0 && (
                      <span className="text-xs text-[#FF2E63]">{formData.tribe.length} selected</span>
                    )}
                    {expandedSections.tribe ? (
                      <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                    )}
                  </div>
                </button>
                {expandedSections.tribe && (
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    {dropdownOptions.tribes?.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(formData.tribe) ? formData.tribe : [];
                          if (current.includes(t)) {
                            setFormData(f => ({ ...f, tribe: current.filter(i => i !== t) }));
                          } else {
                            setFormData(f => ({ ...f, tribe: [...current, t] }));
                          }
                        }}
                        className={`p-2 rounded-lg text-sm capitalize transition-all ${
                          Array.isArray(formData.tribe) && formData.tribe.includes(t)
                            ? 'bg-[#FF2E63]/20 border-2 border-[#FF2E63] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                {!expandedSections.tribe && Array.isArray(formData.tribe) && formData.tribe.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.tribe.map(t => (
                      <span key={t} className="text-xs bg-[#FF2E63]/20 text-[#FF2E63] px-2 py-0.5 rounded-full capitalize">{t}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Sexual Practices - Collapsible */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection('sexual_practices')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <Label className="text-[#EDEDED] cursor-pointer">Sexual Practices</Label>
                  <div className="flex items-center gap-2">
                    {Array.isArray(formData.sexual_practices) && formData.sexual_practices.length > 0 && (
                      <span className="text-xs text-[#FF2E63]">{formData.sexual_practices.length} selected</span>
                    )}
                    {expandedSections.sexual_practices ? (
                      <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                    )}
                  </div>
                </button>
                {expandedSections.sexual_practices && (
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {dropdownOptions.sexual_practices?.map(practice => (
                      <button
                        key={practice}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(formData.sexual_practices) ? formData.sexual_practices : [];
                          if (current.includes(practice)) {
                            setFormData(f => ({ ...f, sexual_practices: current.filter(p => p !== practice) }));
                          } else {
                            setFormData(f => ({ ...f, sexual_practices: [...current, practice] }));
                          }
                        }}
                        className={`p-2 rounded-lg text-sm transition-all ${
                          Array.isArray(formData.sexual_practices) && formData.sexual_practices.includes(practice)
                            ? 'bg-[#FF2E63]/20 border-2 border-[#FF2E63] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {practice}
                      </button>
                    ))}
                  </div>
                )}
                {!expandedSections.sexual_practices && Array.isArray(formData.sexual_practices) && formData.sexual_practices.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.sexual_practices.map(p => (
                      <span key={p} className="text-xs bg-[#FF2E63]/20 text-[#FF2E63] px-2 py-0.5 rounded-full">{p}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Kinks & Freaks - Collapsible */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection('kinks_freaks')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <Label className="text-[#EDEDED] cursor-pointer">Kinks & Freaks</Label>
                  <div className="flex items-center gap-2">
                    {Array.isArray(formData.kinks_freaks) && formData.kinks_freaks.length > 0 && (
                      <span className="text-xs text-[#7000FF]">{formData.kinks_freaks.length} selected</span>
                    )}
                    {expandedSections.kinks_freaks ? (
                      <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                    )}
                  </div>
                </button>
                {expandedSections.kinks_freaks && (
                  <div className="grid grid-cols-3 gap-2 pt-2 max-h-64 overflow-y-auto">
                    {dropdownOptions.kinks_freaks?.map(kink => (
                      <button
                        key={kink}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(formData.kinks_freaks) ? formData.kinks_freaks : [];
                          if (current.includes(kink)) {
                            setFormData(f => ({ ...f, kinks_freaks: current.filter(k => k !== kink) }));
                          } else {
                            setFormData(f => ({ ...f, kinks_freaks: [...current, kink] }));
                          }
                        }}
                        className={`p-2 rounded-lg text-xs transition-all ${
                          Array.isArray(formData.kinks_freaks) && formData.kinks_freaks.includes(kink)
                            ? 'bg-[#7000FF]/20 border-2 border-[#7000FF] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {kink}
                      </button>
                    ))}
                  </div>
                )}
                {!expandedSections.kinks_freaks && Array.isArray(formData.kinks_freaks) && formData.kinks_freaks.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.kinks_freaks.map(k => (
                      <span key={k} className="text-xs bg-[#7000FF]/20 text-[#7000FF] px-2 py-0.5 rounded-full">{k}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* Substance preferences */}
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Smoking</Label>
                  <Select
                    value={formData.smoking}
                    onValueChange={(value) => setFormData(f => ({ ...f, smoking: value }))}
                  >
                    <SelectTrigger data-testid="smoking-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.smoking?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Drinking</Label>
                  <Select
                    value={formData.drinking}
                    onValueChange={(value) => setFormData(f => ({ ...f, drinking: value }))}
                  >
                    <SelectTrigger data-testid="drinking-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.drinking?.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">420</Label>
                  <Select
                    value={formData.cannabis}
                    onValueChange={(value) => setFormData(f => ({ ...f, cannabis: value }))}
                  >
                    <SelectTrigger data-testid="cannabis-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.cannabis?.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Health & Wellness Section */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#08D9D6]" />
                Health & Wellness
              </h3>
              <p className="text-xs text-[#52525B]">This information helps others make informed decisions. Only share what you are comfortable with.</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">HIV Status</Label>
                  <Select
                    value={formData.hiv_status}
                    onValueChange={(value) => setFormData(f => ({ ...f, hiv_status: value }))}
                  >
                    <SelectTrigger data-testid="hiv-status-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.hiv_status?.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#EDEDED]">Last Tested</Label>
                  <Select
                    value={formData.last_tested}
                    onValueChange={(value) => setFormData(f => ({ ...f, last_tested: value }))}
                  >
                    <SelectTrigger data-testid="last-tested-select" className="bg-white/5 border-white/10 h-12">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {dropdownOptions.last_tested?.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Prevention Practices - Collapsible */}
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => toggleSection('prevention_practices')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <Label className="text-[#EDEDED] cursor-pointer">Prevention Practices</Label>
                  <div className="flex items-center gap-2">
                    {Array.isArray(formData.prevention_practices) && formData.prevention_practices.length > 0 && (
                      <span className="text-xs text-[#08D9D6]">{formData.prevention_practices.length} selected</span>
                    )}
                    {expandedSections.prevention_practices ? (
                      <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
                    )}
                  </div>
                </button>
                {expandedSections.prevention_practices && (
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    {dropdownOptions.prevention_practices?.map(practice => (
                      <button
                        key={practice}
                        type="button"
                        onClick={() => {
                          const current = Array.isArray(formData.prevention_practices) ? formData.prevention_practices : [];
                          if (current.includes(practice)) {
                            setFormData(f => ({ ...f, prevention_practices: current.filter(p => p !== practice) }));
                          } else {
                            setFormData(f => ({ ...f, prevention_practices: [...current, practice] }));
                          }
                        }}
                        className={`p-2 rounded-lg text-sm text-center transition-all ${
                          Array.isArray(formData.prevention_practices) && formData.prevention_practices.includes(practice)
                            ? 'bg-[#08D9D6]/20 border-2 border-[#08D9D6] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 border border-white/10'
                        }`}
                      >
                        {practice}
                      </button>
                    ))}
                  </div>
                )}
                {!expandedSections.prevention_practices && Array.isArray(formData.prevention_practices) && formData.prevention_practices.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {formData.prevention_practices.map(p => (
                      <span key={p} className="text-xs bg-[#08D9D6]/20 text-[#08D9D6] px-2 py-0.5 rounded-full">{p}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Availability Status - Looking Right Now */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#FFD700]" />
                Availability Status
              </h3>
              <p className="text-xs text-[#52525B]">Let others know if you are ready to meet. "Available Now" lasts for 60 minutes.</p>
              
              <div className="grid grid-cols-3 gap-2">
                <button
                  data-testid="status-browsing"
                  onClick={() => handleAvailabilityChange('just_browsing')}
                  disabled={updatingAvailability}
                  className={`p-3 rounded-lg text-center transition-all ${
                    availabilityStatus === 'just_browsing'
                      ? 'bg-[#52525B]/30 border-2 border-[#52525B] text-white'
                      : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                  }`}
                >
                  <Eye className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs block">Just Browsing</span>
                </button>
                <button
                  data-testid="status-ready"
                  onClick={() => handleAvailabilityChange('ready_to_meet')}
                  disabled={updatingAvailability}
                  className={`p-3 rounded-lg text-center transition-all ${
                    availabilityStatus === 'ready_to_meet'
                      ? 'bg-[#08D9D6]/20 border-2 border-[#08D9D6] text-[#08D9D6]'
                      : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                  }`}
                >
                  <MapPinned className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs block">Ready to Meet</span>
                </button>
                <button
                  data-testid="status-available"
                  onClick={() => handleAvailabilityChange('available_now')}
                  disabled={updatingAvailability}
                  className={`p-3 rounded-lg text-center transition-all ${
                    availabilityStatus === 'available_now'
                      ? 'bg-[#22c55e]/20 border-2 border-[#22c55e] text-[#22c55e]'
                      : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                  }`}
                >
                  <Zap className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs block">Available Now</span>
                </button>
              </div>
            </div>

            {/* Vibe Check - Voice Intro */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Mic className="w-4 h-4 text-[#FF2E63]" />
                Vibe Check
              </h3>
              <p className="text-xs text-[#52525B]">Record a 30-second voice intro so others can hear your vibe</p>
              
              {voiceIntro || user?.voice_intro ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <audio 
                      src={voiceIntro || user?.voice_intro} 
                      controls 
                      className="flex-1 h-10"
                    />
                    <Button
                      data-testid="delete-voice-intro"
                      variant="ghost"
                      size="icon"
                      onClick={deleteVoiceIntro}
                      className="text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-5 h-5" />
                    </Button>
                  </div>
                  <p className="text-xs text-[#08D9D6]">Your voice intro is live!</p>
                </div>
              ) : audioBlob ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <audio 
                      src={URL.createObjectURL(audioBlob)} 
                      controls 
                      className="flex-1 h-10"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setAudioBlob(null)}
                      className="text-[#A1A1AA] hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <Button
                    data-testid="save-voice-intro"
                    onClick={saveVoiceIntro}
                    className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Voice Intro
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {isRecording ? (
                    <div className="text-center">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 rounded-full mb-3">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                        <span className="text-red-500 font-mono">{recordingTime}s / 30s</span>
                      </div>
                      <Button
                        data-testid="stop-recording"
                        onClick={stopRecording}
                        className="w-full bg-red-500 hover:bg-red-600"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Stop Recording
                      </Button>
                    </div>
                  ) : (
                    <Button
                      data-testid="start-recording"
                      onClick={startRecording}
                      className="w-full bg-white/10 hover:bg-white/20 text-white"
                    >
                      <Mic className="w-4 h-4 mr-2" />
                      Record Voice Intro
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Icebreaker Prompts */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#08D9D6]" />
                Icebreaker Prompts
              </h3>
              <p className="text-xs text-[#52525B]">Help start better conversations with prompts on your profile</p>
              
              {[1, 2, 3].map(num => (
                <div key={num} className="space-y-2 p-3 bg-white/5 rounded-lg">
                  <Select
                    value={formData[`icebreaker_prompt_${num}`]}
                    onValueChange={(value) => setFormData(f => ({ ...f, [`icebreaker_prompt_${num}`]: value }))}
                  >
                    <SelectTrigger data-testid={`icebreaker-prompt-${num}`} className="bg-white/5 border-white/10 h-10">
                      <SelectValue placeholder={`Select prompt ${num}`} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10 max-h-60">
                      {(dropdownOptions.icebreaker_prompts || []).map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData[`icebreaker_prompt_${num}`] && (
                    <Input
                      data-testid={`icebreaker-answer-${num}`}
                      value={formData[`icebreaker_${num}`]}
                      onChange={(e) => setFormData(f => ({ ...f, [`icebreaker_${num}`]: e.target.value }))}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Your answer..."
                      maxLength={150}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Local Guide Toggle */}
            <div className="glass rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPinned className={`w-5 h-5 ${formData.is_local_guide ? 'text-[#FFD700]' : 'text-[#52525B]'}`} />
                  <div>
                    <h3 className="text-sm font-semibold text-[#A1A1AA]">Local Guide</h3>
                    <p className="text-xs text-[#52525B]">
                      Help visitors explore your city
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="local-guide-switch"
                  checked={formData.is_local_guide}
                  onCheckedChange={(val) => setFormData(f => ({ ...f, is_local_guide: val }))}
                  className="data-[state=checked]:bg-[#FFD700]"
                />
              </div>
              {formData.is_local_guide && (
                <p className="text-xs text-[#FFD700] bg-[#FFD700]/10 p-2 rounded">
                  Travelers will be able to see you are a local guide and request to connect!
                </p>
              )}
            </div>

            {/* Save Button */}
            <Button
              data-testid="save-profile-btn"
              onClick={handleSave}
              disabled={loading}
              className="w-full py-6 font-bold uppercase tracking-wider bg-[#FF2E63] hover:bg-[#E01E50] text-white rounded-full btn-glow"
            >
              <Save className="w-5 h-5 mr-2" />
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-6">
            {/* Public Photos */}
            <div className="glass rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Public Photos
                </h3>
                <span className="text-xs text-[#52525B]">{user?.public_photos?.length || 0}/10</span>
              </div>
              
              <div className="photo-gallery">
                {user?.public_photos?.map((photo, i) => (
                  <div key={i} className="relative aspect-square group">
                    <img
                      src={getPhotoUrl(photo)}
                      alt={`Public ${i + 1}`}
                      className="w-full h-full object-cover rounded-md"
                    />
                    <button
                      data-testid={`delete-public-photo-${i}`}
                      onClick={() => handleDeletePhoto(i, false)}
                      className="absolute top-1 right-1 p-1.5 bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                ))}
                
                {(user?.public_photos?.length || 0) < 10 && (
                  <button
                    data-testid="add-public-photo-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="aspect-square flex items-center justify-center bg-white/5 border border-dashed border-white/20 rounded-md hover:border-[#FF2E63]/50 transition-colors"
                  >
                    {uploadingPhoto ? (
                      <div className="w-6 h-6 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-8 h-8 text-[#52525B]" />
                    )}
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handlePhotoUpload(e, false)}
                className="hidden"
              />
            </div>

            {/* Private Photos */}
            <div className="glass rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Private Photos
                </h3>
                <span className="text-xs text-[#52525B]">{user?.private_photos?.length || 0}/10</span>
              </div>
              <p className="text-xs text-[#52525B]">Private photos are blurred until you choose to reveal them</p>
              
              <div className="photo-gallery">
                {user?.private_photos?.map((photo, i) => (
                  <div key={i} className="relative aspect-square group">
                    <img
                      src={getPhotoUrl(photo)}
                      alt={`Private ${i + 1}`}
                      className="w-full h-full object-cover rounded-md"
                    />
                    <button
                      data-testid={`delete-private-photo-${i}`}
                      onClick={() => handleDeletePhoto(i, true)}
                      className="absolute top-1 right-1 p-1.5 bg-black/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                    <div className="absolute bottom-1 right-1">
                      <Lock className="w-4 h-4 text-white/70" />
                    </div>
                  </div>
                ))}
                
                {(user?.private_photos?.length || 0) < 10 && (
                  <button
                    data-testid="add-private-photo-btn"
                    onClick={() => privateFileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="aspect-square flex items-center justify-center bg-white/5 border border-dashed border-white/20 rounded-md hover:border-[#FF2E63]/50 transition-colors"
                  >
                    {uploadingPhoto ? (
                      <div className="w-6 h-6 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <Lock className="w-6 h-6 text-[#52525B]" />
                        <Camera className="w-6 h-6 text-[#52525B]" />
                      </div>
                    )}
                  </button>
                )}
              </div>
              <input
                ref={privateFileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handlePhotoUpload(e, true)}
                className="hidden"
              />
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA]">Account</h3>
              
              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Email</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="bg-white/5 border-white/10 text-[#52525B] h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#EDEDED]">Username</Label>
                <Input
                  value={user?.username || ''}
                  disabled
                  className="bg-white/5 border-white/10 text-[#52525B] h-12"
                />
              </div>
            </div>

            {/* My Membership Section */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Crown className="w-4 h-4" />
                My Membership
              </h3>
              
              {membership ? (
                <>
                  {/* Current Tier Display */}
                  <div className={`p-4 rounded-lg ${
                    membership.tier === 'elite' 
                      ? 'bg-gradient-to-r from-[#FFD700]/20 to-[#FFA500]/20 border border-[#FFD700]/30' 
                      : membership.tier === 'premium'
                      ? 'bg-gradient-to-r from-[#FF2E63]/20 to-[#7000FF]/20 border border-[#FF2E63]/30'
                      : 'bg-white/5 border border-white/10'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {membership.tier === 'elite' ? (
                          <Crown className="w-6 h-6 text-[#FFD700]" />
                        ) : membership.tier === 'premium' ? (
                          <Star className="w-6 h-6 text-[#FF2E63]" />
                        ) : (
                          <Users className="w-6 h-6 text-[#A1A1AA]" />
                        )}
                        <span className={`text-lg font-bold ${
                          membership.tier === 'elite' ? 'text-[#FFD700]' 
                            : membership.tier === 'premium' ? 'text-[#FF2E63]' 
                            : 'text-white'
                        }`}>
                          {membership.tier_name}
                        </span>
                      </div>
                      {membership.expires && (
                        <span className="text-xs text-[#A1A1AA]">
                          Expires: {new Date(membership.expires).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    
                    {/* Usage Stats */}
                    <div className="space-y-2 mt-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#A1A1AA]">Taps Today</span>
                        <span className="text-white">
                          {membership.limits?.taps_used_today || 0} / {
                            membership.limits?.taps_per_day === 9999 
                              ? '∞' 
                              : membership.limits?.taps_per_day || 10
                          }
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        <div 
                          className="bg-[#FF2E63] h-2 rounded-full transition-all"
                          style={{ 
                            width: membership.limits?.taps_per_day === 9999 
                              ? '5%' 
                              : `${Math.min(100, ((membership.limits?.taps_used_today || 0) / (membership.limits?.taps_per_day || 10)) * 100)}%` 
                          }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between text-sm mt-3">
                        <span className="text-[#A1A1AA]">Profiles Viewable</span>
                        <span className="text-white">
                          {membership.limits?.grid_limit === 9999 
                            ? 'Unlimited' 
                            : membership.limits?.grid_limit || 50
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Upgrade CTA */}
                  {membership.tier !== 'elite' && (
                    <Button
                      data-testid="upgrade-membership-btn"
                      onClick={() => toast.info('Upgrade feature coming soon!')}
                      className={`w-full py-4 font-semibold ${
                        membership.tier === 'free' 
                          ? 'bg-gradient-to-r from-[#FF2E63] to-[#7000FF] hover:opacity-90'
                          : 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black hover:opacity-90'
                      }`}
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      {membership.tier === 'free' 
                        ? 'Upgrade to Premium' 
                        : 'Upgrade to Elite'
                      }
                    </Button>
                  )}

                  {/* Feature List */}
                  {membership.features && (
                    <div className="space-y-2 pt-2">
                      <p className="text-xs text-[#52525B] font-medium uppercase">Your Features</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(membership.features).slice(0, 6).map(([key, enabled]) => (
                          <div 
                            key={key} 
                            className={`flex items-center gap-2 text-xs p-2 rounded ${
                              enabled ? 'text-[#08D9D6]' : 'text-[#52525B]'
                            }`}
                          >
                            {enabled ? (
                              <div className="w-4 h-4 rounded-full bg-[#08D9D6]/20 flex items-center justify-center">
                                <div className="w-2 h-2 rounded-full bg-[#08D9D6]" />
                              </div>
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-white/5" />
                            )}
                            <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>

            {/* Visibility Control */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA]">Visibility</h3>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isVisible ? (
                    <Wifi className="w-5 h-5 text-[#08D9D6]" />
                  ) : (
                    <WifiOff className="w-5 h-5 text-[#52525B]" />
                  )}
                  <div>
                    <Label className="text-[#EDEDED]">Show on Grid</Label>
                    <p className="text-xs text-[#52525B]">
                      {isVisible ? 'You are visible to others' : 'You are hidden from the grid'}
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="visibility-switch"
                  checked={isVisible}
                  onCheckedChange={handleToggleVisibility}
                  disabled={togglingVisibility}
                  className="data-[state=checked]:bg-[#08D9D6]"
                />
              </div>
            </div>

            {/* Push Notifications */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Push Notifications
              </h3>
              
              {!permissionGranted ? (
                <div className="space-y-3">
                  <p className="text-sm text-[#A1A1AA]">
                    Enable push notifications to get alerted about new messages, taps, and matches.
                  </p>
                  <Button
                    data-testid="enable-notifications-btn"
                    onClick={requestPermission}
                    className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    Enable Notifications
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageCircle className="w-5 h-5 text-[#08D9D6]" />
                      <Label className="text-[#EDEDED]">New Messages</Label>
                    </div>
                    <Switch
                      data-testid="notify-messages-switch"
                      checked={preferences?.messages ?? true}
                      onCheckedChange={(val) => updatePreferences({ messages: val })}
                      className="data-[state=checked]:bg-[#08D9D6]"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-[#FF2E63]" />
                      <Label className="text-[#EDEDED]">Taps & Matches</Label>
                    </div>
                    <Switch
                      data-testid="notify-taps-switch"
                      checked={preferences?.taps ?? true}
                      onCheckedChange={(val) => updatePreferences({ taps: val })}
                      className="data-[state=checked]:bg-[#FF2E63]"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Eye className="w-5 h-5 text-[#7000FF]" />
                      <Label className="text-[#EDEDED]">Profile Views</Label>
                    </div>
                    <Switch
                      data-testid="notify-views-switch"
                      checked={preferences?.profile_views ?? true}
                      onCheckedChange={(val) => updatePreferences({ profile_views: val })}
                      className="data-[state=checked]:bg-[#7000FF]"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#FFD700]" />
                      <Label className="text-[#EDEDED]">Event Reminders</Label>
                    </div>
                    <Switch
                      data-testid="notify-events-switch"
                      checked={preferences?.events ?? true}
                      onCheckedChange={(val) => updatePreferences({ events: val })}
                      className="data-[state=checked]:bg-[#FFD700]"
                    />
                  </div>
                  
                  <div className="border-t border-white/10 pt-3 mt-3">
                    <p className="text-xs text-[#52525B] mb-3">In-App Alerts</p>
                    
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#A1A1AA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                          <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                          <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                        </svg>
                        <Label className="text-[#EDEDED]">Sound</Label>
                      </div>
                      <Switch
                        data-testid="notify-sound-switch"
                        checked={preferences?.sound ?? true}
                        onCheckedChange={(val) => updatePreferences({ sound: val })}
                        className="data-[state=checked]:bg-[#08D9D6]"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#A1A1AA]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4v16"/>
                          <path d="M9 4v16"/>
                          <path d="M14 4v16"/>
                          <path d="M19 4v16"/>
                        </svg>
                        <Label className="text-[#EDEDED]">Vibration</Label>
                      </div>
                      <Switch
                        data-testid="notify-vibration-switch"
                        checked={preferences?.vibration ?? true}
                        onCheckedChange={(val) => updatePreferences({ vibration: val })}
                        className="data-[state=checked]:bg-[#08D9D6]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Incognito Mode */}
            <div className="glass rounded-xl p-4 space-y-4">
              <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                <Ghost className="w-4 h-4" />
                Incognito Mode
              </h3>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Ghost className={`w-5 h-5 ${isIncognito ? 'text-[#7000FF]' : 'text-[#52525B]'}`} />
                  <div>
                    <Label className="text-[#EDEDED]">Browse Invisibly</Label>
                    <p className="text-xs text-[#52525B]">
                      {isIncognito ? 'You won\'t appear in grids or record views' : 'Your profile views are tracked'}
                    </p>
                  </div>
                </div>
                <Switch
                  data-testid="incognito-switch"
                  checked={isIncognito}
                  onCheckedChange={handleToggleIncognito}
                  disabled={togglingIncognito}
                  className="data-[state=checked]:bg-[#7000FF]"
                />
              </div>
              <p className="text-xs text-[#A1A1AA]">
                When enabled, you can browse profiles without appearing in their Viewed Me list and will not show up in nearby grids.
              </p>
            </div>

            {/* Blocked Users */}
            <div className="glass rounded-xl p-4">
              <Button
                data-testid="view-blocked-btn"
                onClick={() => navigate('/blocked')}
                variant="ghost"
                className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/10"
              >
                <Ban className="w-5 h-5 mr-3" />
                Blocked Users
              </Button>
            </div>

            {/* Help & FAQ */}
            <div className="glass rounded-xl p-4">
              <Button
                data-testid="help-faq-btn"
                onClick={() => navigate('/faq')}
                variant="ghost"
                className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/10"
              >
                <HelpCircle className="w-5 h-5 mr-3" />
                Help & FAQ
              </Button>
            </div>

            {/* Contact Us */}
            <div className="glass rounded-xl p-4">
              <Button
                data-testid="contact-btn"
                onClick={() => navigate('/contact')}
                variant="ghost"
                className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/10"
              >
                <MessageSquare className="w-5 h-5 mr-3 text-[#FF2E63]" />
                Contact Us
              </Button>
            </div>

            {/* Safety Center */}
            <div className="glass rounded-xl p-4">
              <Button
                data-testid="safety-center-btn"
                onClick={() => navigate('/safety')}
                variant="ghost"
                className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/10"
              >
                <Shield className="w-5 h-5 mr-3 text-[#08D9D6]" />
                Safety Center
              </Button>
            </div>

            {/* LGBTQ+ Resources */}
            <div className="glass rounded-xl p-4">
              <Button
                data-testid="resources-btn"
                onClick={() => navigate('/resources')}
                variant="ghost"
                className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/10"
              >
                <MapPinned className="w-5 h-5 mr-3 text-[#FF2E63]" />
                LGBTQ+ Resources
              </Button>
            </div>

            {/* Profile Verification */}
            <div className="glass rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4" />
                  Profile Verification
                </h3>
                {user?.is_verified && (
                  <span className="px-2 py-1 bg-[#08D9D6]/10 text-[#08D9D6] text-xs rounded-full font-medium">
                    Verified
                  </span>
                )}
              </div>
              
              {user?.is_verified ? (
                <div className="flex items-center gap-3 p-3 bg-[#08D9D6]/10 rounded-lg">
                  <BadgeCheck className="w-6 h-6 text-[#08D9D6]" />
                  <div>
                    <p className="text-[#08D9D6] font-medium">Verified Profile</p>
                    <p className="text-xs text-[#A1A1AA]">Your identity has been confirmed</p>
                  </div>
                </div>
              ) : user?.verification_status === 'pending' ? (
                <div className="flex items-center gap-3 p-3 bg-yellow-500/10 rounded-lg">
                  <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                  <div>
                    <p className="text-yellow-500 font-medium">Verification Pending</p>
                    <p className="text-xs text-[#A1A1AA]">Your request is being reviewed</p>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-[#A1A1AA]">
                    Get a verified badge to show others you are real. Take a selfie with a specific pose and we will review it.
                  </p>
                  <Button
                    data-testid="get-verified-btn"
                    onClick={() => navigate('/verify')}
                    className="w-full bg-[#08D9D6] hover:bg-[#08D9D6]/80 text-black font-semibold"
                  >
                    <BadgeCheck className="w-5 h-5 mr-2" />
                    Get Verified
                  </Button>
                </>
              )}
            </div>

            <div className="glass rounded-xl p-4">
              <Button
                data-testid="logout-settings-btn"
                onClick={() => setShowLogoutModal(true)}
                variant="ghost"
                className="w-full justify-start text-red-500 hover:text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Log Out
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <BottomNav />

      {/* Logout Modal */}
      <Dialog open={showLogoutModal} onOpenChange={setShowLogoutModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Log Out</DialogTitle>
          </DialogHeader>
          <p className="text-[#A1A1AA]">Are you sure you want to log out?</p>
          <DialogFooter className="gap-3">
            <Button
              data-testid="cancel-logout-btn"
              variant="ghost"
              onClick={() => setShowLogoutModal(false)}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-logout-btn"
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Log Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyProfile;
